// Calendario: convierte lo que el backend ya tiene (viajes y novedades) en
// eventos de calendario. Derivación pura, sin IO ni React.
//
// El calendario NO tiene contrato propio (no hay `/events` en openapi.yaml, y
// no lo necesita): lo que se PROGRAMA son los viajes (`/trips`) y lo que
// PASÓ y se dejó escrito son las novedades (`/logbook-entries`). Un evento de
// calendario es solo una forma de verlos sobre el eje del tiempo.
//
// Tres decisiones que no se deducen de los campos:
//
//  1. **El viaje se pinta en su `window`, no en su `scheduled*`.** Es la
//     ventana que gobierna el expediente (ADR-024): un viaje que salió tarde o
//     que se corrigió después ocupa el calendario donde realmente ocurrió. Un
//     viaje CANCELADO no tiene ventana (viene colapsada), así que cae a lo que
//     se había programado — y se dibuja apagado, no se esconde: que una salida
//     se cancelara es información que un supervisor quiere ver.
//  2. **La novedad es un instante, no un intervalo.** Se le da una franja fija
//     corta solo para que el calendario pueda dibujarla; su duración no
//     significa nada.
//  3. **Los filtros de conductor y vehículo son de cliente**, sobre lo ya
//     cargado del rango visible. La visibilidad por rol, en cambio, la aplica
//     el servidor (contrato §7): nada de esto decide quién ve qué.

import {
  addHours, addMinutes, endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek,
} from 'date-fns';
import type { Trip } from '../types/trip';
import type { LogbookEntry } from '../types/logbook';
import { TRIP_STATUS_COLORS, TRIP_STATUS_LABELS } from './trips';
import { colorDeTipo } from './logbook';

export type CalendarKind = 'trip' | 'logbook';
export type CalendarViewName = 'month' | 'week' | 'day';

export interface CalendarItem {
  /** `trip:<id>` o `logbook:<id>`: un viaje y una novedad pueden compartir uuid de forma teórica; el prefijo lo hace imposible. */
  id: string;
  kind: CalendarKind;
  title: string;
  start: Date;
  end: Date;
  color: string;
  /** Estado del viaje o tipo de la novedad, en texto. */
  badge: string;
  /** Viaje cancelado: se dibuja apagado y tachado. */
  cancelled: boolean;
  vehicleId?: string;
  vehiclePlate?: string;
  driverId?: string;
  driverName?: string;
  /** Exactamente uno de los dos, según `kind`. */
  trip?: Trip;
  entry?: LogbookEntry;
}

/** Duración con la que se dibuja una novedad, que en realidad es un instante. */
const LOGBOOK_BLOCK_MINUTES = 30;

/** Un rango válido, o el inicio + `fallbackHours` si el backend trajo algo imposible. */
function safeRange(fromIso: string, toIso: string, fallbackHours: number): { start: Date; end: Date } {
  const start = new Date(fromIso);
  const end = new Date(toIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return { start, end: addHours(start, fallbackHours) };
  }
  return { start, end };
}

export function tripToItem(trip: Trip): CalendarItem {
  const from = trip.window?.from ?? trip.scheduledFrom;
  const to = trip.window?.to ?? trip.scheduledTo;
  const { start, end } = safeRange(from, to, 1);
  const cancelled = trip.status === 'cancelled';

  return {
    id: `trip:${trip.id}`,
    kind: 'trip',
    title: `${trip.origin} → ${trip.destination}`,
    start,
    end,
    color: cancelled ? '#6B7280' : TRIP_STATUS_COLORS[trip.status],
    badge: TRIP_STATUS_LABELS[trip.status],
    cancelled,
    vehicleId: trip.vehicleId,
    vehiclePlate: trip.vehiclePlate,
    driverId: trip.driverId,
    driverName: trip.driverName,
    trip,
  };
}

export function logbookToItem(entry: LogbookEntry): CalendarItem {
  const start = new Date(entry.occurredAt);
  return {
    id: `logbook:${entry.id}`,
    kind: 'logbook',
    title: entry.entryTypeLabel,
    start,
    end: addMinutes(start, LOGBOOK_BLOCK_MINUTES),
    color: colorDeTipo(entry.entryTypeCode),
    badge: 'Novedad',
    cancelled: false,
    vehicleId: entry.vehicleId,
    vehiclePlate: entry.vehiclePlate,
    driverId: entry.driverId,
    driverName: entry.driverName,
    entry,
  };
}

/**
 * El rango que el calendario está mostrando, para pedir al servidor solo eso.
 *
 * En Mes son las seis semanas de la cuadrícula, no solo el mes: los días de
 * relleno de los bordes también se pintan y no deben aparecer vacíos.
 * Semanas desde el lunes, igual que el `localizer` de la página.
 */
export function visibleRange(view: CalendarViewName, date: Date): { start: Date; end: Date } {
  const opts = { weekStartsOn: 1 as const };
  if (view === 'month') {
    return { start: startOfWeek(startOfMonth(date), opts), end: endOfWeek(endOfMonth(date), opts) };
  }
  if (view === 'week') {
    return { start: startOfWeek(date, opts), end: endOfWeek(date, opts) };
  }
  return { start: startOfDay(date), end: endOfDay(date) };
}

export interface CalendarFilters {
  kind: CalendarKind | 'all';
  /** `''` = todos. */
  driverId: string;
  vehicleId: string;
}

export function applyFilters(items: CalendarItem[], filters: CalendarFilters): CalendarItem[] {
  return items.filter(item => {
    if (filters.kind !== 'all' && item.kind !== filters.kind) return false;
    if (filters.driverId && item.driverId !== filters.driverId) return false;
    if (filters.vehicleId && item.vehicleId !== filters.vehicleId) return false;
    return true;
  });
}
